import { CRYPTO_ASSETS, QUICK_AMOUNTS } from '../../../lib/constants'
import { config } from '../../../config'
import { formatMYR, formatCrypto } from '../../../lib/utils'
import { layout, ui, cx, text } from '../../../lib/ui-primitives'
import { BackIcon } from '../../icons'
import type { useKiosk } from '../../../hooks/useKiosk'

interface AmountScreenProps {
  kiosk: ReturnType<typeof useKiosk>
}

export function AmountScreen({ kiosk }: AmountScreenProps) {
  const {
    selectedCrypto,
    selectedNetwork,
    amount,
    setAmount,
    prices,
    networkFee,
    cryptoAmount,
    error,
    setError,
    submitAmount,
    setScreen,
  } = kiosk

  if (!selectedCrypto || !selectedNetwork) return null

  const asset = CRYPTO_ASSETS[selectedCrypto]

  const goBack = () => {
    if (asset.networks.length > 1) {
      setScreen('network')
    } else {
      setScreen('welcome')
    }
  }

  return (
    <div className={layout.screenContent}>
      <button
        onClick={goBack}
        className={ui.backBtn}
        aria-label="Go back"
      >
        <BackIcon aria-hidden="true" /> <span>Back</span>
      </button>

      <div className="text-center mb-4 sm:mb-6">
        <div
          className={cx(ui.pill, 'mb-3')}
          style={{ backgroundColor: `${asset.color}18` }}
        >
          <span aria-hidden="true">{asset.icon}</span>
          <span className="text-white font-medium">{selectedCrypto}</span>
          <span className="text-white/30">•</span>
          <span className={cx('text-sm', text.secondary)}>{selectedNetwork}</span>
        </div>
        <h2 className={text.h2}>Enter Amount</h2>
      </div>

      <div className="flex-1 flex flex-col items-center overflow-auto">
        <div className="w-full max-w-sm">
          {/* Amount Input */}
          <div className="relative mb-4">
            <span className={cx('absolute left-4 top-1/2 -translate-y-1/2 text-xl font-medium', text.secondary)}>
              RM
            </span>
            <input
              type="number"
              inputMode="decimal"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value)
                setError('')
              }}
              placeholder="0.00"
              className={cx(
                ui.input,
                'pl-14 py-4 sm:py-5 text-xl sm:text-2xl font-mono text-center'
              )}
              aria-label="Amount in Malaysian Ringgit"
            />
          </div>

          {/* Quick Amount Buttons */}
          <div className="flex flex-wrap gap-2 justify-center mb-4 sm:mb-6">
            {QUICK_AMOUNTS.map((qa) => (
              <button
                key={qa}
                onClick={() => setAmount(qa.toString())}
                className={cx(
                  ui.btnBase,
                  ui.btnGhost,
                  'py-2 px-4 min-h-[40px] text-sm'
                )}
              >
                {qa}
              </button>
            ))}
          </div>

          {/* Summary Card */}
          {amount && parseFloat(amount) > networkFee && (
            <div className={cx(ui.card, 'p-4 mb-4')}>
              <div className="space-y-2 text-sm">
                <div className={cx('flex justify-between', text.secondary)}>
                  <span>You pay</span>
                  <span className="text-white">{formatMYR(amount)}</span>
                </div>
                <div className={cx('flex justify-between', text.secondary)}>
                  <span>Network fee ({selectedNetwork})</span>
                  <span>-{formatMYR(networkFee)}</span>
                </div>
                <div className={cx('flex justify-between', text.secondary)}>
                  <span>Rate</span>
                  <span className={text.numeric}>
                    1 {selectedCrypto} = {formatMYR(prices[selectedCrypto] || 0)}
                  </span>
                </div>
                <div className={cx(ui.divider, 'pt-2 mt-2')}>
                  <div className="flex justify-between items-center">
                    <span className="text-white font-medium">You receive</span>
                    <span
                      className={cx('font-mono font-bold text-base sm:text-lg', text.numeric)}
                      style={{ color: asset.color }}
                    >
                      {formatCrypto(cryptoAmount, 4)} {selectedCrypto}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className={cx(ui.error, 'mb-4')} role="alert">
              {error}
            </div>
          )}

          {/* Continue Button */}
          <button
            onClick={submitAmount}
            disabled={!amount || parseFloat(amount) <= networkFee}
            className={cx(ui.btnBase, ui.btnPrimary, 'w-full py-4 font-bold text-lg')}
            style={{ backgroundColor: asset.color, color: 'white' }}
          >
            Continue
          </button>

          <p className={cx('text-center text-xs mt-4', text.disabled)}>
            Min: {formatMYR(config.minAmount)} • Max: {formatMYR(config.maxAmount)}
          </p>
        </div>
      </div>
    </div>
  )
}
